import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import EnumArticleReviewStatus from "../../../utils/enum/EnumArticleReviewStatus";
import EnumYesNo from "../../../utils/enum/EnumYesNo";
import MessageType from "../../../utils/MessageType";
import { handleErrorMessage, richTextStateFromHTML, richTextStateToHtml, richTextValue } from "../../../utils/Utils";

interface ReviewServiceType {
  article: any;
  setReviewState: any;
  reviewState: any;
  submitReview: any;
  showBlockUI: boolean;
  generalRef: any;
  majorRef: any;
  minorRef: any;
  commentsAbstractRef: any;
  commentsTablesRef: any;
  commentsFiguresRef: any;
  commentsReferencesRef: any;
  commentsToEditorRef: any;
}

let reviewAutoSave: any = {};
var autoSave: any = undefined;

export default function ReviewService(): ReviewServiceType {
  const { showMessage, setBlockUI } = useAppContext();
  const { articleID, articleReviewerId }: any = useParams();

  const history = useHistory();

  const { axiosGetArticleById, axiosPostDraftReview, axiosGetReview, axiosPostSubmitReview }: ArticleControllerType =
    ArticleController();

  const [showBlockUI, setShowBlockUI] = useState<boolean>(false);
  const [article, setArticle] = useState();
  const [reviewState, setReviewState] = useState<any>({
    significance: undefined,
    originality: undefined,
    design: undefined,
    impact: undefined,
    relevance: undefined,
    abstractQuality: undefined,
    conclusions: undefined,
    referencing: undefined,
    rating: undefined,

    recommendation: undefined,

    violation: undefined,
    editorialNeeded: undefined,
    volunteer: undefined,

    date: "2021-09-03",
    revisions: 0,
  });

  const generalRef = useRef<any>();
  const majorRef = useRef<any>();
  const minorRef = useRef<any>();
  const commentsAbstractRef = useRef<any>();
  const commentsTablesRef = useRef<any>();
  const commentsFiguresRef = useRef<any>();
  const commentsReferencesRef = useRef<any>();
  const commentsToEditorRef = useRef<any>();

  useEffect(() => {
    reviewAutoSave = reviewState;
  }, [reviewState]);

  const getArticleById = () => {
    axiosGetArticleById(articleID)
      .then((result: any) => {
        setArticle(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  useEffect(() => {
    getArticleById();
    getReviewByID(articleID, articleReviewerId);
    return () => {
      clearInterval(autoSave);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getReviewByID = (articleID: number, reviewID: number) => {
    setBlockUI(true);
    axiosGetReview(articleID, reviewID)
      .then((resposne: any) => {
        const { review, status } = resposne.data;
        if (review) {
          generalRef.current.set(richTextStateFromHTML(review.general));
          majorRef.current.set(richTextStateFromHTML(review.major));
          minorRef.current.set(richTextStateFromHTML(review.minor));
          commentsAbstractRef.current.set(richTextStateFromHTML(review.commentsAbstract));
          commentsTablesRef.current.set(richTextStateFromHTML(review.commentsTables));
          commentsFiguresRef.current.set(richTextStateFromHTML(review.commentsFigures));
          commentsReferencesRef.current.set(richTextStateFromHTML(review.commentsReferences));
          commentsToEditorRef.current.set(richTextStateFromHTML(review.commentsToEditor));

          const data = {
            significance: review.significance,
            originality: review.originality,
            design: review.design,
            impact: review.impact,
            relevance: review.relevance,
            abstractQuality: review.abstractQuality,
            conclusions: review.conclusions,
            referencing: review.referencing,
            rating: review.rating,
            recommendation: review.recommendation,
            violation:
              review.violation === true
                ? EnumYesNo.YES.toString()
                : review.violation === false
                ? EnumYesNo.NO.toString()
                : undefined,
            editorialNeeded:
              review.editorialNeeded === true
                ? EnumYesNo.YES.toString()
                : review.editorialNeeded === false
                ? EnumYesNo.NO.toString()
                : undefined,

            volunteer:
              review.volunteer === true
                ? EnumYesNo.YES.toString()
                : review.volunteer === false
                ? EnumYesNo.NO.toString()
                : undefined,
          };

          setReviewState(data);
        }
        if (
          [
            EnumArticleReviewStatus.MAILED,
            EnumArticleReviewStatus.COMPLETED,
            EnumArticleReviewStatus.DECLINED,
          ].includes(status)
        ) {
          setShowBlockUI(true);
        } else {
          setShowBlockUI(false);
          autoSave = setInterval(() => {
            postDraftReview();
          }, 30000);
        }
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      })
      .finally(() => setBlockUI(false));
  };

  const validate = () => {
    if (!reviewState.significance) {
      showMessage("Significance of the research is required.", MessageType.ERROR);
    } else if (!reviewState.originality) {
      showMessage("Orginality of research is required.", MessageType.ERROR);
    } else if (!reviewState.design) {
      showMessage("Experimental design and quality of data is required.", MessageType.ERROR);
    } else if (!reviewState.impact) {
      showMessage("Impact on clinical practice is required.", MessageType.ERROR);
    } else if (!reviewState.relevance) {
      showMessage("Relevance/interest to rhinology is required.", MessageType.ERROR);
    } else if (!reviewState.abstractQuality) {
      showMessage("Quality of abstract is required.", MessageType.ERROR);
    } else if (!reviewState.conclusions) {
      showMessage("Conclusions is required.", MessageType.ERROR);
    } else if (!reviewState.referencing) {
      showMessage("Adequate referencing is required.", MessageType.ERROR);
    } else if (!reviewState.rating) {
      showMessage("If adequately revised the potential rating of the paper could be is required.", MessageType.ERROR);
    } else if (!reviewState.recommendation) {
      showMessage("Overal Recommendation is required.", MessageType.ERROR);
    } else if (!reviewState.violation) {
      showMessage(
        "Is there any question of violation of the journals policy on research involving animals and human beings? is required.",
        MessageType.ERROR
      );
    } else if (!reviewState.editorialNeeded) {
      showMessage("Is an editorial needed? is required.", MessageType.ERROR);
    } else if (!reviewState.volunteer) {
      showMessage(
        "if an editorial is needed, are you willing to write one within 2 weeks? is required.",
        MessageType.ERROR
      );
    } else if (!richTextValue(generalRef).getCurrentContent().hasText()) {
      showMessage("General Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(majorRef).getCurrentContent().hasText()) {
      showMessage("Major Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(minorRef).getCurrentContent().hasText()) {
      showMessage("Minor Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(commentsAbstractRef).getCurrentContent().hasText()) {
      showMessage("Abstract Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(commentsTablesRef).getCurrentContent().hasText()) {
      showMessage("Table Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(commentsFiguresRef).getCurrentContent().hasText()) {
      showMessage("Figure Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(commentsReferencesRef).getCurrentContent().hasText()) {
      showMessage("References Comments is required.", MessageType.ERROR);
    } else if (!richTextValue(commentsToEditorRef).getCurrentContent().hasText()) {
      showMessage("Comments to the editor is required.", MessageType.ERROR);
    } else {
      return true;
    }

    return false;
  };

  const submitReview = () => {
    if (validate()) {
      setBlockUI(true);
      axiosPostSubmitReview(articleID, articleReviewerId, convertData())
        .then((response: any) => {
          showMessage("Review successfully submitted.", MessageType.SUCCESS);
          history.push("/reviewerToDoList");
        })
        .catch((error: any) => {
          handleErrorMessage(showMessage, error);
        })
        .finally(() => setBlockUI(false));
    }
  };

  const convertBooleanValue = (booleanValue: any) => {
    if (booleanValue === EnumYesNo.YES.toString()) {
      return true;
    } else if (booleanValue === EnumYesNo.NO.toString()) {
      return false;
    }
    return undefined;
  };

  const convertData = () => {
    return {
      ...reviewAutoSave,
      reviewId: articleReviewerId,
      violation: convertBooleanValue(reviewAutoSave.violation),
      editorialNeeded: convertBooleanValue(reviewAutoSave.editorialNeeded),
      volunteer: convertBooleanValue(reviewAutoSave.volunteer),
      general: richTextStateToHtml(richTextValue(generalRef)),
      major: richTextStateToHtml(richTextValue(majorRef)),
      minor: richTextStateToHtml(richTextValue(minorRef)),
      commentsAbstract: richTextStateToHtml(richTextValue(commentsAbstractRef)),
      commentsTables: richTextStateToHtml(richTextValue(commentsTablesRef)),
      commentsFigures: richTextStateToHtml(richTextValue(commentsFiguresRef)),
      commentsReferences: richTextStateToHtml(richTextValue(commentsReferencesRef)),
      commentsToEditor: richTextStateToHtml(richTextValue(commentsToEditorRef)),
    };
  };

  const postDraftReview = () => {
    axiosPostDraftReview(articleID, articleReviewerId, convertData())
      .then((response: any) => {
        const { success, message } = response.data;
        if (!success) {
          setShowBlockUI(true);
          showMessage(message, MessageType.ERROR);
        } else if (showBlockUI) {
          setShowBlockUI(false);
        } else {
          showMessage("Draft saved.", MessageType.SUCCESS);
        }
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  return {
    article,
    reviewState,
    setReviewState,
    submitReview,
    showBlockUI,
    generalRef,
    majorRef,
    minorRef,
    commentsAbstractRef,
    commentsTablesRef,
    commentsFiguresRef,
    commentsReferencesRef,
    commentsToEditorRef,
  };
}

export type { ReviewServiceType };
